import {
  Box,
  Button,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  Select,
  Typography,
} from "@mui/material";
import { grey } from "@mui/material/colors";
import { AlertContext } from "contexts/Alert";
import {
  Dispatch,
  SetStateAction,
  useContext,
  useEffect,
  useState,
} from "react";
import { CustomEvent, Map } from "schemas/functions";
import { routes } from "schemas/routes";

import StyledTextField from "components/Styled/TextField";
import { isPage } from "utils/general";
import { hasSocialMediaProfile } from "utils/influencer";
import { makeDeepCopy } from "utils/updateLocalState";

import styles from "../styles";
import {
  SocialMediaFieldKey,
  SocialMediaFields,
  SocialMediaLinks,
  convertOnboardingFormToLink,
  isValidUsername,
  sanitizeInput,
  stripHttp,
} from "./helpers";

interface Props {
  validation: {
    [key in string]: string | undefined;
  };
  onboardingForm: Map;
  setOnboardingForm: Dispatch<SetStateAction<Map>>;
  setLinksValidation: Dispatch<
    SetStateAction<{
      [key in string]: string | undefined;
    }>
  >;
  links: SocialMediaLinks[];
  setLinks: Dispatch<SetStateAction<SocialMediaLinks[]>>;
  activeQuestionIndex?: number;
  error?: string;
}

const AboutFormLinks = ({
  validation,
  onboardingForm,
  setOnboardingForm,
  setLinksValidation,
  links,
  setLinks,
  activeQuestionIndex,
  error,
}: Props) => {
  const { setAlert } = useContext(AlertContext);
  const allKeys = Object.values(SocialMediaFields)?.map((value) => value.key);
  const hasSocialLink = hasSocialMediaProfile(onboardingForm);
  const isActive = activeQuestionIndex === 3;
  const [filled, setFilled] = useState(hasSocialLink);
  const [isInteracting, setIsInteracting] = useState(false);

  useEffect(() => {
    if (isActive) {
      setFilled(false);
    }
  }, [isActive]);

  useEffect(() => {
    if (links?.length === 0 && hasSocialLink) {
      const links = convertOnboardingFormToLink(onboardingForm);
      setLinks(links);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasSocialLink]);

  const handleChangeField = (
    e: CustomEvent,
    currentFieldName: SocialMediaFieldKey,
  ) => {
    setLinks((prev) => {
      const copy = makeDeepCopy(prev);
      const index = copy.findIndex(
        (link: SocialMediaLinks) => link.fieldName === currentFieldName,
      );
      if (index > -1) {
        copy[index].fieldName = e.target.value as SocialMediaFieldKey;
        copy[index].link = "";
        handleSave(currentFieldName, "");
      }
      return copy;
    });
  };

  const handleAddLink = () => {
    const remaining = getRemainingLinks();
    if (remaining?.length > 0) {
      setLinks((prev) => {
        const copy = makeDeepCopy(prev);
        copy.push({
          fieldName: remaining[0] as SocialMediaFieldKey,
          link: "",
        });
        return copy;
      });
    }
  };

  const handleDeleteLink = (fieldName: SocialMediaFieldKey) => {
    if (links?.length <= 1) {
      setAlert("You must input at least one link", "warning");
      return;
    }
    setLinks((prev) => {
      const copy = makeDeepCopy(prev);
      const index = copy.findIndex(
        (link: SocialMediaLinks) => link.fieldName === fieldName,
      );
      if (index > -1) {
        copy.splice(index, 1);
      }
      handleSave(fieldName, "");
      return copy;
    });
  };

  const getRemainingLinks = () => {
    const existing = links?.map((link) => link.fieldName);
    const remaining = allKeys.filter(
      (item) => !existing?.includes(item as SocialMediaFieldKey),
    );
    return remaining;
  };

  const handleSave = (fieldName: string, value: string) => {
    setOnboardingForm((prev) => ({
      ...prev,
      [`${fieldName}Url`]: value,
    }));
  };

  const handleBlurred = (e: CustomEvent) => {
    e.stopPropagation();
    const { name, value } = e.target;
    const sanitizedInput =
      name !== SocialMediaFieldKey.portfolio
        ? sanitizeInput(value.trim())
        : stripHttp(value.trim());
    if (name !== SocialMediaFieldKey.portfolio) {
      if (sanitizedInput && !isValidUsername(sanitizedInput)) {
        setLinksValidation((prev) => ({
          ...prev,
          [name]:
            "Must contain only letters, numbers, underscores, hyphens or periods",
        }));
      } else {
        setLinksValidation((prev) => ({
          ...prev,
          [name]: undefined,
        }));
      }
    }
  };

  const handleChanged = (e: CustomEvent) => {
    e.stopPropagation();
    const { name, value } = e.target;
    const sanitizedInput =
      name !== SocialMediaFieldKey.portfolio
        ? sanitizeInput(value.trim())
        : stripHttp(value.trim());
    if (name === SocialMediaFieldKey.portfolio) {
      handleSave(name, `https://${sanitizedInput}`);
    } else {
      handleSave(
        name,
        `${SocialMediaFields[`${name}Url`]?.url}${sanitizedInput}`,
      );
      if (isValidUsername(value)) {
        setLinksValidation((prev) => ({
          ...prev,
          [name]: undefined,
        }));
      }
    }
  };

  const renderOneLink = (fieldName: SocialMediaFieldKey, link: string) => {
    return (
      <Grid
        container
        columnSpacing={2}
        rowGap={1}
        key={fieldName}
        alignItems="center"
        sx={{ mb: 2 }}
      >
        <Grid item xs={12} md="auto">
          <Select
            sx={
              !!validation[fieldName]
                ? [styles.selectSocial, { mb: 2.5 }]
                : styles.selectSocial
            }
            value={fieldName}
            onChange={(e) => {
              e.stopPropagation();

              handleChangeField(e, fieldName);
            }}
          >
            <MenuItem key={fieldName} value={fieldName}>
              {SocialMediaFields[`${fieldName}Url`]?.name}
            </MenuItem>
            {getRemainingLinks()?.map((key) => (
              <MenuItem key={key} value={key}>
                {SocialMediaFields[`${key}Url`]?.name}
              </MenuItem>
            ))}
          </Select>
        </Grid>
        <Grid container item xs={12} md alignItems="center">
          <Grid item xs>
            <StyledTextField
              fullWidth
              placeholder={
                fieldName === SocialMediaFieldKey.portfolio
                  ? "website.com"
                  : "handle"
              }
              name={fieldName}
              defaultValue={link}
              required
              InputProps={{
                sx: { borderRadius: 4 },
                startAdornment: (
                  <InputAdornment
                    sx={styles.linkAdornment}
                    disablePointerEvents
                    position="start"
                  >
                    {SocialMediaFields[`${fieldName}Url`]?.url}
                  </InputAdornment>
                ),
              }}
              onBlur={handleBlurred}
              onChange={handleChanged}
              error={!!validation[fieldName]}
              helperText={validation[fieldName]}
            />
          </Grid>
          <Grid item xs="auto">
            <IconButton
              sx={{
                color: "purple.main",
                fontSize: 14,
              }}
              onClick={() => handleDeleteLink(fieldName)}
            >
              <Box component="i" className="fa-regular fa-trash" />
            </IconButton>
          </Grid>
        </Grid>
      </Grid>
    );
  };
  const getDisplayValue = () => {
    return links
      .map((link) => SocialMediaFields[`${link.fieldName}Url`]?.name)
      .filter(Boolean)
      .join(", ");
  };

  if (filled) {
    return (
      <Grid
        alignItems="center"
        gap={2}
        container
        onClick={() => setFilled(false)}
        sx={{
          border: `1px solid ${grey[300]}`,
          borderRadius: "16px",
          p: 2,
          width: "100%",
          mb: 2,
          cursor: "pointer",
        }}
      >
        <Grid item xs>
          <Typography gutterBottom sx={{ color: "#151F16", fontSize: 12 }}>
            Social Links *
          </Typography>
          <Typography variant="body1" sx={{ color: "black", fontWeight: 500 }}>
            {getDisplayValue()}
          </Typography>
        </Grid>
        <Grid item xs="auto">
          <Box
            component="i"
            className="fa-regular fa-check"
            sx={{ color: "green.main" }}
          />
        </Grid>
      </Grid>
    );
  }

  return (
    <Box
      sx={{
        border: isActive ? "2px solid" : `1px solid ${grey[300]}`,
        borderColor: isActive ? "green.main" : grey[300],
        borderRadius: "16px",
        p: 2,
      }}
      onBlur={(e) => {
        if (isInteracting) return;
        if (hasSocialLink) {
          setFilled(true);
        }
      }}
      onMouseDown={() => setIsInteracting(true)}
      onMouseUp={() => setIsInteracting(false)}
    >
      <Typography gutterBottom>
        {isPage(routes.onboardingProAbout)
          ? "Social links and portfolio"
          : "Please share your most active social links below"}
      </Typography>

      {error && (
        <Typography gutterBottom sx={{ color: "error.main" }}>
          {error}
        </Typography>
      )}
      {links?.map((link) => renderOneLink(link.fieldName, link.link))}
      {getRemainingLinks()?.length > 0 && (
        <Grid container>
          <Button onClick={handleAddLink} disableElevation sx={{ mb: 2 }}>
            Add Link +
          </Button>
        </Grid>
      )}
    </Box>
  );
};

export default AboutFormLinks;
